import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Button,
  Typography,
  TextField,
  Select,
  MenuItem,
  Grid,
  IconButton,
  FormControlLabel,
  Checkbox,
} from "@material-ui/core";
import { Add, AttachMoney, Cancel, CheckCircle, Delete, Edit, EmojiEvents, Star } from "@material-ui/icons";

import { gradeDepartmentLevel } from "../../../../reducer/studentResearch/action";
import { uploadMultiFile } from "../../../../reducer/file/action";
import MultiFileManager from "../../../File/MultiFileManager/MultiFileManager";
import select from "../../../../utils/select";
import SectionCard from "../../utils/SectionCard";
import LevelNoteField from "../../utils/LevelNoteField";
import {
  MAIN_PRIZE_OPTIONS,
  MAIN_PRIZE_PASS,
  REVIEW_ACTION_ACCEPT,
  REVIEW_ACTION_REJECT,
  REVIEW_ACTION_ADJUST_REQUEST,
} from "../../../../constants/projectStatus";

// DepartmentLevelForm — "Nghiệm thu cấp Khoa" gộp (refactor 2026-07-23):
// chấm điểm + kết luận nghiệm thu + gán giải trong 1 hành động duy nhất,
// xuất phát từ council_assigned. Kết quả/giải phụ/đề xuất cấp Trường chỉ áp
// dụng khi Duyệt.
export default function DepartmentLevelForm({ detail, onDone }) {
  const dispatch = useDispatch();
  const profileDetail = useSelector((s) => select(s, "profileReducer", "detail"));

  const parseUrls = (s) => (s ? String(s).split("||").filter(Boolean) : []);
  const [point, setPoint] = useState(detail?.point || 0);
  const [estimateUrls, setEstimateUrls] = useState(parseUrls(detail?.estimateFileLink));
  const [estimateNewFiles, setEstimateNewFiles] = useState([]);
  const [comment, setComment] = useState(detail?.acceptanceComment || "");
  const [note, setNote] = useState(detail?.departmentLevelNote || "");
  const [result, setResult] = useState(detail?.mainPrize || MAIN_PRIZE_PASS);
  const [eligible, setEligible] = useState(!!detail?.eligible);
  const [sidePrizes, setSidePrizes] = useState(detail?.sidePrizes || []);
  const [pending, setPending] = useState(null);

  useEffect(() => {
    setPoint(detail?.point || 0);
    setEstimateUrls(parseUrls(detail?.estimateFileLink));
    setEstimateNewFiles([]);
    setComment(detail?.acceptanceComment || "");
    setNote(detail?.departmentLevelNote || "");
    setResult(detail?.mainPrize || MAIN_PRIZE_PASS);
    setEligible(!!detail?.eligible);
    setSidePrizes(detail?.sidePrizes || []);
  }, [detail?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const addPrize = () =>
    setSidePrizes([...sidePrizes, { name: "", description: "", level: "" }]);
  const removePrize = (idx) => setSidePrizes(sidePrizes.filter((_, i) => i !== idx));
  const updatePrize = (idx, key, val) => {
    const next = sidePrizes.slice();
    next[idx] = { ...next[idx], [key]: val };
    setSidePrizes(next);
  };

  const commitEstimateFiles = async () => {
    if (!estimateNewFiles || estimateNewFiles.length === 0) return estimateUrls.join("||");
    const ownerId = profileDetail?.id || 0;
    const uploadResult = await dispatch(uploadMultiFile(ownerId, estimateNewFiles, "studentResearch"));
    if (uploadResult?.success && Array.isArray(uploadResult.data)) {
      const merged = [...estimateUrls, ...uploadResult.data];
      setEstimateUrls(merged);
      setEstimateNewFiles([]);
      return merged.join("||");
    }
    return estimateUrls.join("||");
  };

  const handle = async (action) => {
    if (
      (action === REVIEW_ACTION_ADJUST_REQUEST || action === REVIEW_ACTION_REJECT) &&
      !comment.trim()
    ) {
      alert("Vui lòng nhập nhận xét / lý do.");
      return;
    }
    setPending(action);
    try {
      const estimateStr = await commitEstimateFiles();
      const ok = await dispatch(
        gradeDepartmentLevel(detail.id, {
          point: Number(point),
          estimateFileLink: estimateStr,
          comment,
          note,
          action,
          result: action === REVIEW_ACTION_ACCEPT ? result : "",
          eligible: action === REVIEW_ACTION_ACCEPT ? eligible : false,
          sidePrizes: action === REVIEW_ACTION_ACCEPT ? sidePrizes : [],
        })
      );
      if (ok && onDone) onDone();
    } finally {
      setPending(null);
    }
  };

  return (
    <Box padding="16px 24px">
      <SectionCard icon={<AttachMoney />} title="Chấm điểm nghiệm thu" marginTop={0}>
        <Grid container spacing={2}>
          <Grid item xs={3}>
            <Typography variant="body2" style={{ marginBottom: 6 }}>
              <b>Điểm</b> <span style={{ color: "#c62828" }}>*</span>
            </Typography>
            <TextField
              fullWidth
              type="number"
              size="small"
              variant="outlined"
              value={point}
              onChange={(e) => {
                const v = e.target.value;
                if (v === "") {
                  setPoint(0);
                  return;
                }
                const n = parseFloat(v);
                if (!isNaN(n)) setPoint(Math.min(100, Math.max(0, n)));
              }}
              inputProps={{ min: 0, max: 100, step: 1 }}
            />
          </Grid>
          <Grid item xs={9}>
            <Typography variant="body2" style={{ marginBottom: 6 }}>
              <b>Phiếu đánh giá nghiệm thu</b>
            </Typography>
            <MultiFileManager
              fileUrls={estimateUrls}
              setFileUrls={setEstimateUrls}
              newFiles={estimateNewFiles}
              setNewFiles={setEstimateNewFiles}
              disabled={false}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body2" style={{ marginBottom: 6 }}>
              Nhận xét
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={3}
              variant="outlined"
              placeholder="Bắt buộc khi Yêu cầu chỉnh sửa hoặc Loại"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />
          </Grid>
          <LevelNoteField value={note} onChange={setNote} />
        </Grid>
      </SectionCard>

      <SectionCard icon={<EmojiEvents />} title="Kết quả (áp dụng khi Duyệt)">
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={6}>
            <Typography variant="body2" style={{ marginBottom: 6 }}>
              Kết quả <span style={{ color: "#c62828" }}>*</span>
            </Typography>
            <Select
              fullWidth
              variant="outlined"
              margin="dense"
              value={result}
              onChange={(e) => setResult(e.target.value)}
            >
              {MAIN_PRIZE_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
          </Grid>
          <Grid item xs={6}>
            <FormControlLabel
              control={
                <Checkbox
                  color="primary"
                  checked={eligible}
                  onChange={(e) => setEligible(e.target.checked)}
                />
              }
              label="Đề xuất báo cáo tại Hội nghị SVNCKH cấp trường"
            />
          </Grid>
        </Grid>
      </SectionCard>

      <SectionCard icon={<Star />} title="Giải phụ (tuỳ chọn)">
        {sidePrizes.length === 0 && (
          <Typography variant="body2" color="textSecondary">
            Chưa có giải phụ. Ấn "Thêm giải phụ" để bắt đầu.
          </Typography>
        )}
        {sidePrizes.map((p, i) => (
          <Box
            key={`sp-${i}`}
            padding={2}
            marginTop={i === 0 ? 0 : 2}
            bgcolor="#fafafa"
            borderRadius={4}
            border="solid 1px #e0e0e0"
          >
            <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom={1}>
              <Typography variant="body2" color="textSecondary">
                <b>Giải phụ #{i + 1}</b>
              </Typography>
              <IconButton size="small" onClick={() => removePrize(i)}>
                <Delete fontSize="small" />
              </IconButton>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={5}>
                <TextField
                  fullWidth
                  size="small"
                  variant="outlined"
                  label="Tên giải phụ"
                  value={p.name}
                  onChange={(e) => updatePrize(i, "name", e.target.value)}
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  size="small"
                  variant="outlined"
                  label="Mô tả"
                  value={p.description}
                  onChange={(e) => updatePrize(i, "description", e.target.value)}
                />
              </Grid>
              <Grid item xs={3}>
                <TextField
                  fullWidth
                  size="small"
                  variant="outlined"
                  label="Mức giải"
                  placeholder="Nhất/Nhì/Ba..."
                  value={p.level}
                  onChange={(e) => updatePrize(i, "level", e.target.value)}
                />
              </Grid>
            </Grid>
          </Box>
        ))}
        <Box marginTop={2}>
          <Button size="small" variant="outlined" startIcon={<Add />} onClick={addPrize}>
            Thêm giải phụ
          </Button>
        </Box>
      </SectionCard>

      <Box
        marginTop={3}
        paddingTop={2}
        display="flex"
        gridGap={12}
        justifyContent="flex-end"
        borderTop="solid 1px #e0e0e0"
      >
        <Button
          variant="contained"
          style={{ backgroundColor: "#b52f2f", color: "#fff" }}
          startIcon={<Cancel />}
          disabled={pending !== null}
          onClick={() => handle(REVIEW_ACTION_REJECT)}
        >
          Loại
        </Button>
        <Button
          variant="outlined"
          color="secondary"
          startIcon={<Edit />}
          disabled={pending !== null}
          onClick={() => handle(REVIEW_ACTION_ADJUST_REQUEST)}
        >
          Yêu cầu chỉnh sửa
        </Button>
        <Button
          variant="contained"
          color="primary"
          startIcon={<CheckCircle />}
          disabled={pending !== null}
          onClick={() => handle(REVIEW_ACTION_ACCEPT)}
        >
          Duyệt
        </Button>
      </Box>
    </Box>
  );
}
